import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { colors, fontSize } from '../../constants/styleConstants';
import { usePageScroll } from '../../utils/hooks/usePageScroll';

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: colors.white,
    top: 83,
    boxShadow: 'none',
    borderBottom: '1px solid rgb(58, 58, 58, 0.2)',
    maxWidth: '100%',
    zIndex: 900,
    color: colors.primary2,
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      top: 110,
    },
    padding: '0 20px',
  },
  selected: {
    '& .MuiTab-wrapper': {
      fontWeight: 'bold',
      color: `${colors.primary2} !important`,
      opacity: '1 !important',
    },
  },
  tabWrapper: {
    color: colors.primary2,
    fontWeight: 'bold',
  },
  tabRoot: {
    minWidth: 'fit-content',
    padding: '6px 15px',
  },
  badge: {
    borderRadius: '11.5px',
    fontSize: fontSize.smallTitle,
    color: '#fff',
    background: '#3a3a3a',
    display: 'inline-block',
    minWidth: '28px',
    height: '19px',
  },
  subTitle: {
    fontSize: fontSize.smallTitle,
    fontWeight: 'normal',
    textTransform: 'none',
    fontStyle: 'italic',
  },
  selectedBadge: {
    '& .MuiTab-wrapper': {
      '& > div >  span': {
        background: `${colors.primary2} !important`,
      },
    },
  },
}));

export const FbTabsSection = ({
  tabs,
  scrollable,
  onChange,
  initialTabValue,
  value,
  orientation,
  appBarClass,
  tabsClasses,
  tabClasses,
  tabModel,
  preventPageScroll,
}) => {
  const classes = useStyles();
  const isScrollable = scrollable !== false;

  const { setPreventPageScroll } = usePageScroll();

  const handleChange = (event, newValue) => {
    if (preventPageScroll) {
      setPreventPageScroll(true);
    }
    onChange(newValue);
  };

  return (
    <AppBar position={isScrollable ? `sticky` : 'static'} className={cn(classes.appBar, appBarClass)}>
      <Tabs
        value={value || initialTabValue}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="primary"
        textColor="primary"
        orientation={orientation}
        classes={{ root: tabsClasses }}
        TabIndicatorProps={{
          style:
            tabModel === 'normal'
              ? {
                  backgroundColor: `${colors.primary2}`,
                  height: '4px',
                }
              : {
                  backgroundColor: `${colors.primary2}`,
                  height: '6px',
                  borderTopRightRadius: '10px',
                  borderTopLeftRadius: '10px',
                },
        }}
      >
        {tabs.map(item => {
          const name = get(item, 'name', '');
          return (
            <Tab
              classes={{
                selected: cn(classes.selected, item.count && classes.selectedBadge),
                wrapper: classes.tabWrapper,
                root: tabModel === 'normal' ? tabClasses : classes.tabRoot,
                ...(item?.classes || {}),
              }}
              value={get(item, 'value', '')}
              id={item?.value}
              key={`tab-${name}`}
              label={
                <div className={classes.item}>
                  {name} {item.count && <span className={classes.badge}>{item.count}</span>}
                  {item?.subTitle && <span className={classes.subTitle}>{item.subTitle}</span>}
                </div>
              }
            ></Tab>
          );
        })}
      </Tabs>
    </AppBar>
  );
};

FbTabsSection.propTypes = {
  initialTabValue: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      href: PropTypes.string,
    }),
  ).isRequired,
  scrollable: PropTypes.bool,
  onChange: PropTypes.func,
  orientation: PropTypes.string,
  tabsClasses: PropTypes.any,
  tabClasses: PropTypes.any,
  preventPageScroll: PropTypes.bool,
};

FbTabsSection.defaultProps = {
  scrollable: false,
  orientation: 'horizontal',
  tabClasses: {},
  preventPageScroll: true,
};

export default FbTabsSection;
